/**
 * A series of functions for managing page load metrics (namely, sufficiently
 * usable and fully interactive) in a way that is stateful and aware of client
 * side navigation.
 */

// If you see errors about fields on PageView being
// missing, such as a new app name, you likely need to update the import below
// to a newer event version.
// You have to update event-schemas first, so the new version is available.
import type {PageViewV7 as PageView} from "@khanacademy/event-schemas";
import {markMinorConversions} from "@khan/analytics-learning-time";
import {khanFetch} from "@khan/data-fetch";
import {PermissionsInfo, RequestInfo} from "@khan/shared-core";
import type {Entrypoint} from "@khan/shared-core/context";
import type {Location} from "@khan/urls";

import type {TrackedPageSlug} from "./types.ts";

// Fields that you can update in a call to reportPageLifecycleTiming.
// Set a field to true if that state has been reached.  Note that for
// some state-fields, such as firstByteTime, we can figure out the
// right value automatically and do not need to ever be set here.
interface FieldsToUpdate {
    interactive?: boolean;
    usable?: boolean;
}

// sync-start:perf-payload 650940005 https://github.com/Khan/webapp/blob/master/services/analytics/page_perf/page_perf.go
interface PerfData {
    // TODO(csilvers): maybe this can never be null?  Would have to check
    originalRequestId: string | null;
    pageName: TrackedPageSlug;
    navigation: "client" | "server" | "user_action";
    sufficientlyUsable?: number | null;
    fullyInteractive?: number | null;
    firstByte?: number | null;
    eventName?: string;
    eventMs?: number;
}
// sync-end:perf-payload

interface State {
    epoch: number;
    clientSide: boolean;
    currentPageName: TrackedPageSlug | null;
    startTime: number;
    hasReportedSufficientlyUsable: boolean;
    hasReportedFullyInteractive: boolean;
    hasReportedFirstByte: boolean;
    eventTime: Record<string, number>;
}

const FAKE_REQUEST_ID = "12345";

// This is the only mutable global state in the whole file!
let state: State = {
    epoch: 0,
    clientSide: false,
    currentPageName: null,
    startTime: 0,
    hasReportedSufficientlyUsable: false,
    hasReportedFullyInteractive: false,
    hasReportedFirstByte: false,
    eventTime: {},
};

const update = (s: State) => (state = s);

// This is the property to look at on `window` to find any "early" timing
// information for pages that are marked sufficiently_usable before any of
// our JavaScript actually mounts and runs.
const EARLY_USABLE_WINDOW_PROP = "__analyticsPageUsableTimings";

// This is the property to look at on `window` to find the performance
// numbers for the currently loaded page.  (It will be `{}` if the
// current page doesn't call any page-lifecycle functions.)  This is
// intended for use by Cypress (e2e) tests, which can use it to
// wait until the currently-visited page is sufficiently usable or
// fully interactive.  It is used in lib/end_to_end_testutil/browser.py.
const CURRENT_ANALYTICS_WINDOW_PROP = "__analyticsTimings";

// URL used for logging page performance
const PAGE_PERFORMANCE_LOG_URL = "/api/internal/_bb/page_perf_log";

// Naming used for performence events
// These events are used to create performance trace events to assists
// validating the accuracy of the page lifecycle.
const PERFORMANCE_START_EVENT = "pageStart";
const PERFORMANCE_SUFFICIENTLY_USABLE_EVENT = "sufficientlyUsable";
const PERFORMANCE_FULLY_INTERACTIVE_EVENT = "fullyInteractive";

export const getEarlyUsableWindowProp = (): string => {
    return EARLY_USABLE_WINDOW_PROP;
};

const resetEarlyUsableIndicators = () => {
    window[EARLY_USABLE_WINDOW_PROP] = {};
};

const earlyUsableTimeForPage = (pageName: TrackedPageSlug) =>
    window[EARLY_USABLE_WINDOW_PROP] &&
    window[EARLY_USABLE_WINDOW_PROP][pageName];

export const getTimingSummaryProp = (): string => {
    return CURRENT_ANALYTICS_WINDOW_PROP;
};

const resetTimingSummaryGlobal = () => {
    window[CURRENT_ANALYTICS_WINDOW_PROP] = {};
};

const updateTimingSummaryGlobal = (summaryInfo: PerfData) => {
    window[CURRENT_ANALYTICS_WINDOW_PROP] = summaryInfo;
};

export const getSufficentlyUsableEventProp = (): string => {
    return PERFORMANCE_SUFFICIENTLY_USABLE_EVENT;
};

const getPageviewData = (currentPath: string, search: string) => {
    return {
        path: currentPath,
        qs: search.slice(1),
        utc: -new Date().getTimezoneOffset(),
    };
};

// Helper that marks a pageview conversion
const markPageviewConversion = (currentPath: string, search: string) => {
    const conversionsWithExtras = [
        {id: "pageview", extra: getPageviewData(currentPath, search)},
    ] as const;
    markMinorConversions(conversionsWithExtras);
};

// Helper for that might mark conversions on the homepage
export const checkMarkHomepageConversion = (currentPath: string) => {
    const isHome = !!currentPath && currentPath.split("/").join("") === "";
    if (isHome) {
        const isLoggedIn = PermissionsInfo.isLoggedIn;
        if (isLoggedIn) {
            markMinorConversions([{id: "pageview_logged_in_homepage"}]);
        } else {
            markMinorConversions([{id: "pageview_logged_out_homepage"}]);
        }
    }
};

// A helper that marks conversions when client-side navigation occurs
export const markNavConversions = (currentPath: string, search: string) => {
    const pageViewData = getPageviewData(currentPath, search);
    const conversionsWithExtras = [
        {id: "pageview", extra: pageViewData},
        {id: "pagerequest", extra: pageViewData},
    ] as const;
    markMinorConversions(conversionsWithExtras);
};

// Actions that update lifecycle state and perform side-effects. It's really
// important for accurate metrics that we modify state in this file in an
// legible and predictable way. Otherwise we risk mis/over/under-reporting
// metrics that give us important information about traffic and performance.
// Please keep all state in the `state` object and modify it with `update`.

export const init = (currentPath: string, search: string) => {
    // Record initial page view in BigBingo
    markPageviewConversion(currentPath, search);
    checkMarkHomepageConversion(currentPath);
    resetTimingSummaryGlobal();
};

export const getPageViewTraitData = (
    location: Location,
    entrypoint: Entrypoint,
): Omit<PageView, "eventType" | "eventSchemaVersion"> => {
    const {pathname, host, search} = location;
    const pageViewTraitData: Omit<
        PageView,
        "eventType" | "eventSchemaVersion"
    > = {
        pageView_remoteCountry: RequestInfo.remoteCountry,
        pageView_isBot: RequestInfo.isBot,
        pageView_isE2E: RequestInfo.isE2E,
        pageView_currentUrlPath: pathname,
        pageView_currentUrlHost: host,
        // NOTE: we want to remove the leading '?' from the search string
        pageView_currentUrlSearch: search.replace(/^[?]/, ""),
        pageView_productContext: entrypoint,
    };

    return pageViewTraitData;
};

export const handleStartRouterNavigation = () => {
    resetEarlyUsableIndicators();
    resetTimingSummaryGlobal();
    if (window.performance) {
        update({
            ...state,
            clientSide: true,
            epoch: state.epoch + 1,
            startTime: window.performance.now(),
            hasReportedSufficientlyUsable: false,
            hasReportedFullyInteractive: false,
        });
        window.performance?.mark?.(PERFORMANCE_START_EVENT);
    }
};

/**
 * Maybe report page usability timings, namely fully_interactive_ms and
 * sufficiently_usable_ms, to an app engine endpoint.
 *
 * Reported metric will be milliseconds since
 * window.performance.timing.navigationStart for a given page type.
 *
 * Reporting will only happen if there is an outstanding client or server
 * navigation. So it's safe to call this function whenever a component
 * updates, assuming the component doesn't know whether the update was due
 * to a page load or not.
 *
 * @param {string} pageName Name of the page. pageNames provided for
 *     this function must be added to
 *         services/analytics/page_perf/tracked_page_slug.go
 *     or datapoints will not be sent to bigquery.
 * @param {interactive} whether the page is now fully interactive
 * @param {usable} whether the page is now sufficiently usable
 * @param {reportingPercent} reportingPercent 1.0 will report all occurrences
 *     and 0.0 will report none.
 * @returns {Promise} A Promise which resolves when the request to send
1 *     data to bigquery is finished.
 */
export const reportPageLifecycleTiming = (
    pageName: TrackedPageSlug,
    fieldsToUpdate: FieldsToUpdate,
    reportingPercent = 1.0,
): Promise<any> => {
    if (!window.performance) {
        return Promise.resolve();
    }

    const nextState: State = {...state};
    const now = window.performance.now();
    const nav = state.clientSide ? "client" : "server";
    let fullyInteractiveTime: number | null = null;
    let sufficientlyUsableTime: number | null = null;
    let firstByteTime: number | null = null;

    nextState.currentPageName = pageName;
    if (fieldsToUpdate.interactive && !state.hasReportedFullyInteractive) {
        fullyInteractiveTime = Math.round(now - state.startTime);
        nextState.hasReportedFullyInteractive = true;
        // Note: although the mark API provide the markOptions:detail,
        //   the extra detail is not written to trace log.  So we instead
        //   embed the pageName into the name.
        window.performance?.mark?.(
            `${PERFORMANCE_FULLY_INTERACTIVE_EVENT}:${pageName}`,
        );
    }
    if (fieldsToUpdate.usable && !state.hasReportedSufficientlyUsable) {
        const earlyUsableTiming = earlyUsableTimeForPage(pageName);
        sufficientlyUsableTime = Math.round(
            (earlyUsableTiming || now) - state.startTime,
        );
        nextState.hasReportedSufficientlyUsable = true;
        // When earlyUsableTiming is set it is from SSR'ed pages.  In those
        // cases we cheat and render as soon as the component is mounted.
        // We will mark those performance in pageload-marker.jsx
        if (!earlyUsableTiming) {
            window.performance?.mark?.(
                `${PERFORMANCE_SUFFICIENTLY_USABLE_EVENT}:${pageName}`,
            );
        }
    }
    if (!state.clientSide && !state.hasReportedFirstByte) {
        firstByteTime =
            window.performance.timing.responseStart -
            window.performance.timing.navigationStart;
        nextState.hasReportedFirstByte = true;
    }
    update(nextState);

    if (
        // Don't bother to log if there's nothing to report.
        fullyInteractiveTime === null &&
        sufficientlyUsableTime === null &&
        firstByteTime === null
    ) {
        return Promise.resolve();
    }

    const perfData: PerfData = {
        originalRequestId: FAKE_REQUEST_ID,
        pageName: pageName,
        navigation: nav,
        sufficientlyUsable: sufficientlyUsableTime,
        fullyInteractive: fullyInteractiveTime,
        firstByte: firstByteTime,
    };

    // Set some global variables that our Cypress tests (aka
    // smoketests) can use to tell that this page is now sufficiently
    // usable/interactive.  They typically want to wait for that
    // before continuing.
    updateTimingSummaryGlobal(perfData);

    // Don't send the actual API call if sampling says not to, unless we are
    // in a test environment.
    // NOTE(somewhatabstract): If this causes E2Es to pollute perf metrics, we
    // should consider:
    //   1. Filtering perf requests with the E2E header from the backend
    //   2. Adding an E2E event that is raised on reporting these timings and
    //      then using that even in E2Es to capture that the timings were
    //      reported.
    if (
        Math.random() >= reportingPercent &&
        !RequestInfo.isE2E &&
        !RequestInfo.isBot
    ) {
        return Promise.resolve();
    }

    return khanFetch(PAGE_PERFORMANCE_LOG_URL, {
        method: "POST",
        headers: {"Content-Type": "application/json"},
        body: JSON.stringify(perfData),
    });
};

export const reportPageInteractiveTiming = (
    pageName: TrackedPageSlug,
): Promise<any> => reportPageLifecycleTiming(pageName, {interactive: true});

/**
 * Maybe report page usability timings for fully_interactive_ms
 * to an app engine endpoint.
 *
 * @param {string} pageName Name of the page. pageNames provided for
 *     this function must be added to
 *         services/analytics/page_perf/tracked_page_slug.go
 *     or datapoints will not be sent to bigquery.
 * @param {onlyForClientSideNavigation} we consider page load timings for pages
 *     loaded via client-side navigation in a separate category from those that
 *     were loaded from a server-side request. We can choose to report page
 *     timing events _only_ in the context of client-side navigation. There
 *     are rare special cases of marking fully-interactive (involving
 *     the youtube API) where this is necessary.
 */
export const reportPageUsableTiming = (
    pageName: TrackedPageSlug,
    onlyForClientSideNavigation = false,
): Promise<undefined> | Promise<any> =>
    onlyForClientSideNavigation && !state.clientSide
        ? Promise.resolve()
        : reportPageLifecycleTiming(pageName, {usable: true});

/**
 * Start measuring timing for current event.
 * This can then be reported with reportEventTiming
 *
 * @param {string} eventName The eventName should be unique for the event you
 *     would like to measure. Only one event of the same name can be measured
 *     at any time.
 */
export const startEventTiming = (eventName: string) => {
    const nextState: State = {...state};
    const now = window.performance.now();
    nextState.eventTime[eventName] = now;
    update(nextState);
};

/**
 * Reported with reportEventTiming
 *
 * @param {string} eventName  Event name passed by startEventTiming
 * @param {string} pageName (optional) override default pageName
 * @param {number} eventMs (optional) event time in Milliseconds - this will
 *     override and remove any previous startEventTiming marker.
 */
export const reportEventTiming = (
    eventName: string,
    pageName?: TrackedPageSlug,
    eventMs?: number,
): Promise<any> => {
    const nextState: State = {...state};
    const now = window.performance.now();

    if (eventMs === undefined) {
        const eventStartTime = nextState.eventTime[eventName];
        if (eventStartTime == null) {
            return Promise.resolve();
        }
        eventMs = now - eventStartTime;
    }

    const perfData: PerfData = {
        originalRequestId: FAKE_REQUEST_ID,
        // We should have page name since this is set by reportFirstByte
        pageName: pageName || nextState.currentPageName || "unknown_pagename",
        navigation: "user_action",
        eventName,
        eventMs: Math.round(eventMs),
    };

    cancelEventTiming(eventName);

    return khanFetch(PAGE_PERFORMANCE_LOG_URL, {
        method: "POST",
        headers: {"Content-Type": "application/json"},
        body: JSON.stringify(perfData),
    });
};

/**
 * Stop measuring timing for current event without reporting it.
 *
 * @param {string} eventName The eventName should be unique for the event you
 *     would like to measure. Only one event of the same name can be measured
 *     at any time.
 */
export const cancelEventTiming = (eventName: string) => {
    const nextState: State = {...state};
    delete nextState.eventTime[eventName];
    update(nextState);
};
